import { Button } from "@/components/ui/baseUi/button"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import type { ExerciseFormikType } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import { TemplateButtons } from "@/modules/template/components/sidebarImport/templateButtons"
import { TemplateWrapper } from "@/modules/template/components/sidebarImport/templateWrapper"
import { useApplyExerciseTemplate } from "@/modules/template/hooks/useTemplate"
import { useTemplateImportActions } from "@/modules/template/stores/useTemplateImportStore"
import { TemplateType } from "@/modules/template/types/templateType"
import { XIcon } from "lucide-react"
import { useCallback, useEffect, useRef } from "react"

type TemplateImportExerciseViewProps = {
  orderIndex: number
  onValidate: (exercise: ExerciseFormikType) => Promise<void>
  onCancel?: () => void
  className?: string
} & React.ComponentProps<"div">

export function TemplateImportExerciseView({
  orderIndex,
  onValidate,
  onCancel,
  className,
  ...props
}: TemplateImportExerciseViewProps) {
  const { mutateAsync: applyExerciseTemplate } = useApplyExerciseTemplate()
  const { clearSelectedTemplate } = useTemplateImportActions()
  const { updateSidebarStackOnTop, hideSidebarStackOnTop, hideSidebar } = useSidebar()
  const onCloseSidebarFnRef = useRef<() => void>(() => {
    return
  })

  const handleValidateExerciseTemplate = async (templateId: string) => {
    try {
      const exercise = await applyExerciseTemplate({
        templateId,
        applyExerciseTemplateInput: { orderIndex },
      }).then((exercise) => {
        return exercise
      })
      await onValidate(SessionBlockExerciseEntity.fromEntityToFormik(exercise))
      toast.success(word("template.exercise.toast.import.success"), {
        classNames: successToastClassName(),
      })
    } catch {
      toast.error(word("template.exercise.toast.import.error"), {
        classNames: errorToastClassName(),
      })
    }
  }

  const onCloseExerciseTemplateImport = useCallback(async () => {
    // Close the sidebar top stack and hide the sidebar
    clearSelectedTemplate()
    hideSidebarStackOnTop()
    // Wait for the sidebar top stack to be hidden before hiding the sidebar
    await new Promise((resolve) => setTimeout(resolve, 50))
    hideSidebar()
  }, [hideSidebarStackOnTop, hideSidebar, clearSelectedTemplate])

  // Defines the custom header buttons for the sidebar top stack
  const customHeaderButtons = [
    <Button
      key="close-template-import-exercise-view"
      variant="outline"
      size="iconMd"
      onClick={() => {
        onCloseSidebarFnRef.current()
      }}
    >
      <XIcon color="currentColor" size={16} />
    </Button>,
  ]

  useEffect(() => {
    // Add the custom header buttons and the left button click handler to the sidebar top stack
    updateSidebarStackOnTop({
      customHeaderButtons,
    })
  }, [])

  useEffect(() => {
    // Set the onCloseSidebarFnRef to the onCloseExerciseTemplateImport function
    onCloseSidebarFnRef.current = () => {
      void onCloseExerciseTemplateImport()
    }
  }, [])

  return (
    <div className={cn("flex flex-1 flex-col gap-4 pt-18", className)} {...props}>
      <div className="flex flex-1 grow flex-col gap-8">
        <Text fontStyle="TL" className="px-6">
          {word("templateType.import.title.exercise")}
        </Text>
        <TemplateWrapper templateType={TemplateType.EXERCISE} />
      </div>

      <TemplateButtons
        className="sticky bottom-0 flex flex-row gap-2 bg-surface-background-default px-6 pt-4 pb-2"
        onValidate={(templateId: string) => {
          clearSelectedTemplate()
          void handleValidateExerciseTemplate(templateId)
        }}
        onCancel={onCancel}
      />
    </div>
  )
}
